import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/shared/components/ui/Badge';
import { CheckCircle2, StickyNote } from 'lucide-react';
import { OneOnOneStatus, OneOnOneMeetingType } from '@momentum/types';
import { cn } from '@/shared/lib/utils';
import { format } from 'date-fns';
import { enUS, he } from 'date-fns/locale';
import { useGetOneOnOneById } from '@/shared/hooks/useOneOnOne';

const statusConfig = {
  [OneOnOneStatus.SCHEDULED]: {
    label: 'scheduled' as const,
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    lineColor: 'border-blue-300',
  },
  [OneOnOneStatus.IN_PROGRESS]: {
    label: 'inProgress' as const,
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    lineColor: 'border-amber-300',
  },
  [OneOnOneStatus.READY_FOR_SUBMISSION]: {
    label: 'readyForSubmission' as const,
    iconBg: 'bg-indigo-100',
    iconColor: 'text-indigo-600',
    lineColor: 'border-indigo-300',
  },
  [OneOnOneStatus.COMPLETED]: {
    label: 'completed' as const,
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    lineColor: 'border-emerald-300',
  },
} as const;

interface MeetingHistoryItemProps {
  meetingId: string;
  schedualedDate?: string;
  status: OneOnOneStatus | string;
  isLast?: boolean;
}

export const MeetingHistoryItem = ({
  meetingId,
  schedualedDate,
  status,
  isLast = false,
}: MeetingHistoryItemProps) => {
  const { t, i18n } = useTranslation('oneOnOne');
  const navigate = useNavigate();

  // Fetch full meeting data to get notes and completed stages
  const { data: fullMeeting } = useGetOneOnOneById(meetingId);

  const statusInfo =
    statusConfig[status as OneOnOneStatus] ||
    statusConfig[OneOnOneStatus.SCHEDULED];

  const locales = {
    en: enUS,
    he: he,
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'MMMM d, yyyy', {
      locale: locales[i18n.language as 'en' | 'he'] || enUS,
    });
  };

  const formatTime = (dateString: string) => {
    return format(new Date(dateString), 'h:mm a', {
      locale: locales[i18n.language as 'en' | 'he'] || enUS,
    });
  };

  const handleClick = () => {
    // Navigate based on status
    if (status === OneOnOneStatus.COMPLETED) {
      navigate({
        to: '/one-on-one/$id/completed',
        params: { id: meetingId },
      });
    } else if (status === OneOnOneStatus.READY_FOR_SUBMISSION) {
      navigate({
        to: '/one-on-one/$id/summary',
        params: { id: meetingId },
      });
    } else {
      navigate({
        to: '/one-on-one/$id/goals-review',
        params: { id: meetingId },
      });
    }
  };

  const notesCount = fullMeeting?.notes?.manager?.length || 0;

  const getMeetingTitle = () => {
    if (schedualedDate) {
      return formatDate(schedualedDate);
    }

    // Fallback when no date is set
    if (fullMeeting?.meetingType === OneOnOneMeetingType.PROFESSIONAL) {
      return t('professionalMeetingTitle');
    }

    return t('oneOnOneMeetingTitle');
  };

  const getMeetingSubtitle = () => {
    if (schedualedDate) {
      return formatTime(schedualedDate);
    }

    // Show status as subtitle when no date
    return t(statusInfo.label);
  };

  return (
    <div className="relative flex gap-4 pb-6 last:pb-0">
      {/* Timeline line and icon — centered vertically to the card */}
      <div className="relative flex flex-col items-center">
        {/* Line from icon center downward through gap to next item's icon */}
        {!isLast && (
          <div
            className={cn(
              'absolute w-0.5 border-s-2 border-dashed',
              statusInfo.lineColor
            )}
            style={{ top: '50%', bottom: 'calc(-1.5rem - 50%)' }}
          />
        )}
        {/* Icon centered vertically */}
        <div className="z-10 flex h-full items-center">
          <div
            className={cn(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
              statusInfo.iconBg
            )}
          >
            <CheckCircle2 className={cn('h-5 w-5', statusInfo.iconColor)} />
          </div>
        </div>
      </div>

      {/* Content */}
      <div
        className="group flex-1 cursor-pointer rounded-lg border border-slate-200 bg-white p-4 transition-all hover:border-primary hover:shadow-md"
        onClick={handleClick}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 space-y-3">
            {/* Title */}
            <div>
              <h3 className="font-semibold text-slate-900">
                {getMeetingTitle()}
              </h3>
              <p className="mt-0.5 text-sm text-muted-foreground">
                {getMeetingSubtitle()}
              </p>
            </div>

            {/* Metadata */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1.5">
                <StickyNote className="h-4 w-4" />
                <span>
                  {notesCount} {t('notes')}
                </span>
              </div>
              {/* <div className="flex items-center gap-1.5">
                <ListChecks className="h-4 w-4" />
                <span>
                  {completedStages}/{totalStages} {t('stages')}
                </span>
              </div> */}
            </div>

            {/* Timeline - Created and Finished */}
            {fullMeeting?.metadata && (
              <div className="space-y-1.5 text-xs text-muted-foreground">
                {fullMeeting.metadata.createdAt && (
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{t('created')}:</span>
                    <span>
                      {formatDate(
                        new Date(fullMeeting.metadata.createdAt).toISOString()
                      )}{' '}
                      {t('at')}{' '}
                      {formatTime(
                        new Date(fullMeeting.metadata.createdAt).toISOString()
                      )}
                    </span>
                  </div>
                )}
                {fullMeeting.metadata.closedAt &&
                  status === OneOnOneStatus.COMPLETED && (
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{t('finished')}:</span>
                      <span>
                        {formatDate(
                          new Date(fullMeeting.metadata.closedAt).toISOString()
                        )}{' '}
                        {t('at')}{' '}
                        {formatTime(
                          new Date(fullMeeting.metadata.closedAt).toISOString()
                        )}
                      </span>
                    </div>
                  )}
              </div>
            )}
          </div>

          {/* Status Badge */}
          <Badge
            className={cn(
              'shrink-0',
              statusInfo.iconBg,
              statusInfo.iconColor,
              'border-transparent'
            )}
            variant="outline"
          >
            {t(statusInfo.label)}
          </Badge>
        </div>
      </div>
    </div>
  );
};
